import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, FileText } from 'lucide-react';
import { formatMoney } from '@tcrd/shared';
import { Button, Card, ErrorState, SectionTitle, Skeleton, Stat, TextField } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { openAuthenticatedFile } from '../../lib/download';

export interface ReportSummary {
  from: string;
  to: string;
  totals: { fare: string; tips: string; total: string; cash: string; transfer: string };
  quick: Record<'today' | 'week' | 'month', { fare: string; tips: string }>;
  pending: { amount: string; count: number };
  rides: Record<string, number>;
  daily: Array<{ date: string; fare: string; tips: string; total: string; rides: number }>;
}

const localToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Santo_Domingo' }).format(new Date());

export const useReportSummary = (from?: string, to?: string, refetchInterval?: number) =>
  useQuery({ queryKey: ['reports', from, to], queryFn: () => api<ReportSummary>('/reports/summary', { query: { from, to } }), refetchInterval });

export function ReportsPage() {
  const toast = useToast();
  const [from, setFrom] = useState(`${localToday().slice(0, 8)}01`);
  const [to, setTo] = useState(localToday());
  const q = useReportSummary(from, to);
  const max = Math.max(1, ...(q.data?.daily.map((d) => Number(d.total)) ?? [1]));

  const exp = (ext: 'csv' | 'pdf') =>
    openAuthenticatedFile(`/reports/export.${ext}?from=${from}&to=${to}`, `reporte-${from}_${to}.${ext}`).catch((e) => toast(errorMessage(e), 'danger'));

  return (
    <Page title="Reportes" back="/driver/more" onRefresh={() => q.refetch()}>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Desde" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          <TextField label="Hasta" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </div>
        {q.isLoading && <Skeleton className="h-40" />}
        {q.error && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} />}
        {q.data && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Total recibido" value={formatMoney(q.data.totals.total)} tone="accent" />
              <Stat label="Tarifas" value={formatMoney(q.data.totals.fare)} />
              <Stat label="Propinas" value={formatMoney(q.data.totals.tips)} />
              <Stat label="Por cobrar" value={formatMoney(q.data.pending.amount)} hint={`${q.data.pending.count} pago(s)`} tone={q.data.pending.count ? 'warn' : 'default'} />
              <Stat label="Efectivo" value={formatMoney(q.data.totals.cash)} />
              <Stat label="Transferencias" value={formatMoney(q.data.totals.transfer)} />
            </div>
            <p className="text-xs text-[var(--text-muted)]">Calculado solo con cobros que confirmaste. Los pagos reportados por clientes y aún no verificados no se cuentan como ingresos.</p>
            <Card className="p-4">
              <SectionTitle title="Por día" />
              {q.data.daily.length === 0 && <p className="text-sm text-[var(--text-muted)]">Sin cobros en el período.</p>}
              <ul className="flex flex-col gap-2">
                {q.data.daily.map((d) => (
                  <li key={d.date} className="text-sm">
                    <div className="flex justify-between"><span>{d.date}</span><span className="font-semibold tabular-nums">{formatMoney(d.total)}</span></div>
                    <div className="mt-1 h-2 rounded-full bg-black/5 dark:bg-white/10 overflow-hidden" aria-hidden>
                      <div className="h-full bg-brand-red" style={{ width: `${(Number(d.total) / max) * 100}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="secondary" icon={<Download className="size-5" />} onClick={() => exp('csv')}>CSV</Button>
              <Button variant="secondary" icon={<FileText className="size-5" />} onClick={() => exp('pdf')}>PDF</Button>
            </div>
          </>
        )}
      </div>
    </Page>
  );
}
